import { useCallback, useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { useQueries } from '@zg-rentals/react-query5';
import { RentalManagerAccountViewZrmPropertiesWebApiHandler_units } from '@zg-rentals/ts-rental-manager-api-client';

import { cleanApiParams } from '../properties/zrm';

import type { AccountViewExpandedPropertyUnitsResponseDto } from '@zg-rentals/ts-rental-manager-api-client';

export type PropertyTableUnitsListParams = {
  includeArchived?: boolean;
  includeUnarchived?: boolean;
  limit?: number;
  searchText?: string;
};

export type UseGetPropertyTableUnitsDataOptions = {
  enabled?: boolean;
};

/**
 * Property-table equivalent of {@link useGetMultiplexUnits}: fetches expanded units for multiple group IDs
 * in parallel with infinite-query style pagination per group against the property-table units endpoint.
 *
 * Pagination flow:
 * - We keep per group an array of startKeyExclusive values: [''], then ['', endKey1], etc.
 * - Each entry becomes one query (first page uses '', next pages use the previous response's pager.endKey).
 * - Calling fetchNextPageForGroup(groupId, pager.endKey) appends that key; on re-render we build one more
 *   query for that group, and useQueries runs it, so the next page is fetched.
 */
export const useGetPropertyTableUnits = (
  params: PropertyTableUnitsListParams,
  groupIds: Array<string>,
  options: UseGetPropertyTableUnitsDataOptions = {},
) => {
  const { enabled = true } = options;
  const { includeArchived, includeUnarchived, limit, searchText = '' } = params;

  // Per group: array of startKeyExclusive values. First is '' (first page); each additional is the previous page's pager.endKey.
  const [startKeysByGroupId, setStartKeysByGroupId] = useState<Record<string, Array<string>>>({});

  // One query per (groupId, startKeyExclusive). Adding a startKey causes one more query and thus one more fetch.
  const queries = groupIds.flatMap((groupId) => {
    const startKeys = startKeysByGroupId[groupId] ?? [''];
    return startKeys.map((startKeyExclusive) => ({
      queryKey: [
        'getPropertyTableUnits',
        searchText,
        includeArchived,
        includeUnarchived,
        limit,
        groupId,
        startKeyExclusive,
      ] as const,
      queryFn: async () => {
        if (!groupId?.trim()) {
          logError({
            error: new Error('useGetPropertyTableUnits: groupId is required'),
            context: 'useGetPropertyTableUnits',
          });

          throw new Error('groupId is required');
        }

        return RentalManagerAccountViewZrmPropertiesWebApiHandler_units(
          cleanApiParams(
            {
              groupId,
              limit,
              searchText,
              includeArchived,
              includeUnarchived,
              startKeyExclusive: startKeyExclusive || undefined,
            },
            ['limit', 'searchText', 'includeArchived', 'includeUnarchived', 'startKeyExclusive'],
          ),
        );
      },
      enabled: enabled && !!groupId,
      meta: {
        tags: ['getPropertyTableUnits', groupId],
        context: {
          api: 'RentalManagerAccountViewZrmPropertiesWebApiHandler_units',
          details: 'useGetPropertyTableUnits',
        },
      },
    }));
  });

  const results = useQueries({ queries });

  const dataByGroupId: Record<string, Partial<AccountViewExpandedPropertyUnitsResponseDto>> = {};
  const hasNextPageByGroupId: Record<string, boolean> = {};
  const isFetchingNextPageByGroupId: Record<string, boolean> = {};
  const isFetchingByGroupId: Record<string, boolean> = {};

  // Merge all pages per group and derive hasNextPage / isFetchingNextPage / isFetchingByGroupId from results.
  let resultIndex = 0;
  for (const groupId of groupIds) {
    const startKeys = startKeysByGroupId[groupId] ?? [''];
    const groupResults = results.slice(resultIndex, resultIndex + startKeys.length);
    const pages = groupResults
      .map((r) => r.data)
      .filter((d): d is AccountViewExpandedPropertyUnitsResponseDto => d != null);
    if (pages.length > 0) {
      dataByGroupId[groupId] = {
        units: pages.flatMap((p) => p.units),
        listingCount: pages[0].listingCount,
        pager: pages[pages.length - 1].pager,
      };
      hasNextPageByGroupId[groupId] = pages[pages.length - 1].pager?.hasMore ?? false;
    }
    // Check every page, not just the first fetching one, so a concurrently refetching earlier
    // page can't hide a genuinely in-flight next page (e.g. after a bulk invalidateQueries).
    isFetchingByGroupId[groupId] = groupResults.some((r) => r?.isFetching);
    isFetchingNextPageByGroupId[groupId] = groupResults.slice(1).some((r) => r?.isFetching);
    resultIndex += startKeys.length;
  }

  /**
   * Load the next page for a group. Appending nextStartKeyExclusive to state causes one more query
   * to be built on the next render, so useQueries runs it and the next page is fetched.
   * Pass the current last page's pager.endKey as nextStartKeyExclusive.
   */
  const fetchNextPageForGroup = useCallback((groupId: string, nextStartKeyExclusive: string) => {
    if (!nextStartKeyExclusive) return;
    setStartKeysByGroupId((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] ?? ['']), nextStartKeyExclusive],
    }));
  }, []);

  return {
    dataByGroupId,
    results,
    isPending: results.some((r) => r.isPending),
    isFetching: results.some((r) => r.isFetching),
    isFetchingByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingNextPageByGroupId,
  };
};
